import { CSSProperties } from 'react';
import { Framework } from '@/framework/instance';
import { getDefaultHierarchyConfig, HIERARCHY_EVENTS, type HierarchyConfig } from './hierarchy';

export class StageToolbar extends Framework {
    private stageToolbarNodeKey = 'stage-toolbar-node-key';
    private visible = true;
    private referenceLineVisible = true;
    private center = false;
    private visibleEventName = 'stage-toolbar-visible-change';
    private panelVisibleEventName = 'stage-toolbar-panel-visible-change';
    private referenceLineVisibleEventName = 'stage-toolbar-reference-line-visible-change';
    private hierarchyToggleEventName = 'stage-toolbar-hierarchy-toggle-change';

    private hierarchyConfig: HierarchyConfig = getDefaultHierarchyConfig();
    private hierarchyPanelPosition: CSSProperties = {};

    private hierarchyAvailability = true;

    /** 添加工具节点 */
    addNode(node: React.ReactNode) {
        this.framework.setCustomData(this.stageToolbarNodeKey, node);
    }

    /** 获取工具节点 */
    getNode() {
        return this.framework.getCustomData(this.stageToolbarNodeKey);
    }

    /** 隐藏工具栏 */
    hide() {
        this.visible = false;
        this.framework.events.emit(this.visibleEventName, false);
    }

    /** 显示工具栏 */
    show() {
        this.visible = true;
        this.framework.events.emit(this.visibleEventName, true);
    }

    isVisible() {
        return this.visible;
    }

    /** 监听工具栏可见性变化 */
    onVisibleChange(callback: (visible: boolean) => void) {
        const handler = this.framework.events.on(this.visibleEventName, callback);

        return () => {
            this.framework.events.off(handler);
        };
    }

    /** 参考线组件是否可见 */
    isReferenceLineVisible() {
        return this.referenceLineVisible;
    }

    showReferenceLine() {
        this.referenceLineVisible = true;
        this.framework.events.emit(this.referenceLineVisibleEventName, true);
    }
    hideReferenceLine() {
        this.referenceLineVisible = false;
        this.framework.events.emit(this.referenceLineVisibleEventName, false);
    }

    /** 监听参考线可见性变化 */
    onReferenceLineVisibleChange(callback: (visible: boolean) => void) {
        const handler = this.framework.events.on(this.referenceLineVisibleEventName, callback);

        return () => {
            this.framework.events.off(handler);
        };
    }

    /** 参考线组件是否在视口居中 */
    isCenter() {
        return this.center;
    }

    setCenter(center: boolean) {
        this.center = center;
    }

    getHierarchyAvailability() {
        return this.hierarchyAvailability;
    }

    setHierarchyAvailability(flag: boolean) {
        this.hierarchyAvailability = flag;
        this.framework.events.emit(HIERARCHY_EVENTS.TOGGLE_AVAILABILITY, flag);
    }

    getHierarchyConfig() {
        return this.hierarchyConfig;
    }

    getHierarchyPanelPosition() {
        return this.hierarchyPanelPosition;
    }

    setHierarchyPanelPosition(position: CSSProperties) {
        this.hierarchyPanelPosition = position;
    }

    onPanelVisible(callback: (type: string) => void) {
        const handler = this.framework.events.on(this.panelVisibleEventName, callback);

        return () => {
            this.framework.events.off(handler);
        };
    }

    emitPanelVisibleChange(type: string) {
        this.framework.events.emit(this.panelVisibleEventName, type);
    }

    extendHierarchyConfig(config: Partial<HierarchyConfig>) {
        const { onlyShowSortableLayers, menuItems, preventHiddenTypes } = config;

        if (onlyShowSortableLayers !== undefined) {
            this.hierarchyConfig.onlyShowSortableLayers = onlyShowSortableLayers;
        }

        if (menuItems !== undefined && menuItems.length > 0) {
            this.hierarchyConfig.menuItems = [...this.hierarchyConfig.menuItems, ...menuItems];
        }

        if (Array.isArray(preventHiddenTypes)) {
            this.hierarchyConfig.preventHiddenTypes = [
                ...this.hierarchyConfig.preventHiddenTypes,
                ...preventHiddenTypes,
            ];
        }
    }

    hierarchyToggleShow() {
        this.framework.events.emit(this.hierarchyToggleEventName);
    }

    onHierarchyToggleShowChange(callback: () => void) {
        const handler = this.framework.events.on(this.hierarchyToggleEventName, callback);

        return () => {
            this.framework.events.off(handler);
        };
    }
}
